import { invariantResponse } from "@epic-web/invariant";
import { Link, Outlet } from "react-router";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import { prisma } from "#app/utils/db.server.ts";
import { getUserImgSrc } from "#app/utils/misc.tsx";
import { type Route } from "./+types/definitions.ts";

export async function loader({ params, request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const page = Number(url.searchParams.get("page")) || 1;
  const limit = 10;

  const author = await prisma.user.findFirst({
    select: {
      id: true,
      displayName: true,
      username: true,
      image: { select: { objectKey: true } },
      _count: { select: { definitions: { where: { status: "approved" } } } },
    },
    where: { username: params.username },
  });

  invariantResponse(author, "Author not found", { status: 404 });

  const definitions = await prisma.definition.findMany({
    select: {
      id: true,
      translationEn: true,
      usageShona: true,
      up: true,
      down: true,
      createdAt: true,
      word: { select: { term: true, slug: true } },
    },
    where: {
      authorId: author.id,
      status: "approved",
    },
    orderBy: { createdAt: "desc" },
    skip: (page - 1) * limit,
    take: limit,
  });

  const totalPages = Math.ceil(author._count.definitions / limit);

  return {
    author,
    definitions,
    pagination: {
      currentPage: page,
      totalPages,
      totalCount: author._count.definitions,
      limit,
    },
  };
}

export default function AuthorDefinitionsRoute({
  loaderData,
}: Route.ComponentProps) {
  const { author, pagination } = loaderData;
  const authorDisplayName = author.displayName ?? author.username;
  const avatarLetter = authorDisplayName?.charAt(0)?.toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background">
      {/* Header */}
      <div className="bg-card border-b border-gray-200 dark:border-border">
        <div className="container py-8">
          <div className="flex items-center gap-6">
            {author.image?.objectKey ? (
              <img
                src={getUserImgSrc(author.image.objectKey)}
                alt={authorDisplayName}
                className="size-20 rounded-full object-cover"
              />
            ) : (
              <div className="size-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-body-xl font-semibold text-foreground">
                {avatarLetter}
              </div>
            )}
            <div>
              <h1 className="text-body-lg font-bold text-foreground">
                {authorDisplayName}
              </h1>
              <p className="text-muted-foreground mt-1">
                {pagination.totalCount}{" "}
                {pagination.totalCount === 1 ? "definition" : "definitions"}
              </p>
              <Link
                to={`/authors/${author.username}`}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium mt-2 inline-flex items-center gap-1"
              >
                <Icon name="arrow-left" size="sm" />
                View profile
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container py-8">
        <Outlet />
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        404: ({ params }) => (
          <div className="container py-20 text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground mb-4">
              Author not found
            </h1>
            <p className="text-gray-600 dark:text-muted-foreground">
              No author with username "{params.username}" exists
            </p>
          </div>
        ),
      }}
    />
  );
}
